/**
 * PATH: packages/email-hub/src/feature/pages/inboxFilterUrlSync.ts
 * FILE_ID: EMAILHUB-FEATURE-PAGES-INBOXFILTERURLSYNC-001
 * PURPOSE: Canonical body of this email pages module. Lifted
 *          from apps/<tenant>/src/features/email/pages/inboxFilterUrlSync.ts
 *          (see docs/architecture/EMAIL_KANBAN_CUTOVER_ARC.md).
 *          Tenants import this via thin re-export shims; edit
 *          THIS file, not the tenant copies.
 * WHY:     Removes operator-visible drift between tenants —
 *          previously every email feature change had to be
 *          mirrored manually in two tenant trees, and concurrent
 *          agents kept letting one side go stale. One canonical
 *          body, both tenants consume from this package.
 * RELATED:
 *   - apps/<tenant>/src/features/email/pages/inboxFilterUrlSync.ts: thin re-export
 *     shim that points back here.
 */

/** The filter keys this hook owns. Any other key in the URL is
 *  left intact when the filter state changes. Keep in lockstep
 *  with the `params.set(...)` calls in `nextInboxFilterUrl` and
 *  in InboxPage's filter-sync effect. */
export const INBOX_FILTER_PARAM_KEYS = [
  'category',
  'search',
  'is_read',
  'is_starred',
  'allocation',
  'job_id',
  // Sender-company scope (exact domain-suffix match). In the URL so the
  // kanban reader's company chip can deep-link "show all from Practicesale"
  // into the hub (operator 2026-07-06: "Why can I not … click it").
  'company',
] as const

export type InboxFilterParamKey = (typeof INBOX_FILTER_PARAM_KEYS)[number]

export interface InboxFilterValues {
  /** Selected category chips (Supplier Bill / Quote Request / …).
   *  Multi-select: serialised as a comma-joined list under the
   *  single ``category`` URL key (back-compatible — an old
   *  ``?category=quote_request`` link parses to ``['quote_request']``). */
  selectedCategories?: string[] | undefined
  /** Free-text search query from the search bar. */
  searchQuery?: string | undefined
  /** Read-state filter — true = read only, false = unread only,
   *  undefined = both. */
  isReadFilter?: boolean | undefined
  /** Starred-state filter — same shape as isReadFilter. */
  isStarredFilter?: boolean | undefined
  /** Board-allocation filter — `allocated` (human put/confirmed it on a
   *  board) or `unallocated`. FEAT-2026-06-10-INBOX-CLEAR-DECK. */
  allocationFilter?: 'allocated' | 'unallocated' | undefined
  /** Linked-job filter — numeric Job id. */
  jobIdFilter?: number | string | undefined
  /** Sender-company scope — lower-cased sender domain
   *  (``practicesale.com.au``). '' / undefined = no company scope. */
  companyDomain?: string | undefined
}

/**
 * Derive the next URLSearchParams for InboxPage's filter-sync
 * effect, preserving unknown keys.
 *
 * Pure I→O — no react, no router, no globals. Safe to test in
 * isolation.
 *
 * @param current  The current URLSearchParams (from
 *   `useSearchParams()[0]`). Treated as immutable — we always
 *   build a fresh URLSearchParams to return.
 * @param filters  The current filter state from InboxPage.
 * @returns A new URLSearchParams with our filter keys set to
 *   their current values and every other key from `current`
 *   preserved as-is. Caller hands this to `setSearchParams`.
 */
export function nextInboxFilterUrl(
  current: URLSearchParams,
  filters: InboxFilterValues,
): URLSearchParams {
  const next = new URLSearchParams(current.toString())

  // 1. Clear OUR keys so the new state can be authoritative.
  //    (Any key we don't list here is left intact — that's the
  //    point of the helper.)
  for (const key of INBOX_FILTER_PARAM_KEYS) {
    next.delete(key)
  }

  // 2. Re-apply our keys from the current filter state. Note we
  //    use `!= null && != ''` semantics rather than just truthy
  //    checks so an explicit `false` for is_read / is_starred is
  //    serialised (operator IS toggling the filter on).
  if (filters.selectedCategories?.length) {
    next.set('category', filters.selectedCategories.join(','))
  }
  if (filters.searchQuery) next.set('search', filters.searchQuery)
  if (filters.isReadFilter !== undefined) next.set('is_read', String(filters.isReadFilter))
  if (filters.isStarredFilter !== undefined) next.set('is_starred', String(filters.isStarredFilter))
  if (filters.allocationFilter) next.set('allocation', filters.allocationFilter)
  if (filters.jobIdFilter !== undefined && filters.jobIdFilter !== '' && filters.jobIdFilter !== null) {
    next.set('job_id', String(filters.jobIdFilter))
  }
  if (filters.companyDomain) next.set('company', filters.companyDomain)

  return next
}

/** Canonical string of OUR filter keys' values in a URL — used by
 *  InboxPage to tell "the URL changed because WE wrote it" apart from
 *  "the URL changed externally" (back/forward, sidebar deep-link while
 *  mounted). Pure I→O. */
export function inboxFilterSignature(params: URLSearchParams): string {
  return INBOX_FILTER_PARAM_KEYS
    .map((k) => `${k}=${params.get(k) ?? ''}`)
    .join('&')
}

/** Parse OUR filter keys out of a URL into the state shape InboxPage
 *  holds. Mirrors the lazy useState initialisers — keep in lockstep. */
export function parseInboxFiltersFromUrl(params: URLSearchParams): {
  selectedCategories: string[]
  searchQuery: string
  isReadFilter: boolean | undefined
  isStarredFilter: boolean | undefined
  allocationFilter: 'allocated' | 'unallocated' | undefined
  jobIdFilter: number | undefined
  companyDomain: string
} {
  const isRead = params.get('is_read')
  const isStarred = params.get('is_starred')
  const allocation = params.get('allocation')
  const jobIdRaw = params.get('job_id')
  const jobId = jobIdRaw ? Number.parseInt(jobIdRaw, 10) : NaN
  // Multi-select: ``category`` is a comma-joined list (back-compatible —
  // a single ``?category=quote_request`` parses to ``['quote_request']``).
  return {
    selectedCategories: (params.get('category') || '')
      .split(',')
      .map((c) => c.trim())
      .filter(Boolean),
    searchQuery: params.get('search') ?? '',
    isReadFilter: isRead === 'true' ? true : isRead === 'false' ? false : undefined,
    isStarredFilter: isStarred === 'true' ? true : isStarred === 'false' ? false : undefined,
    allocationFilter:
      allocation === 'allocated' || allocation === 'unallocated' ? allocation : undefined,
    jobIdFilter: Number.isFinite(jobId) && jobId > 0 ? jobId : undefined,
    companyDomain: (params.get('company') || '').trim().toLowerCase(),
  }
}
